import React, { useCallback, useEffect, useState } from 'react';
import {
  AccountingPeriodReturn,
  BalanceSheet,
  IncomeStatement,
  IncomeStatementSection,
  TrialBalance,
  TrialBalanceRow,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import { Divider } from '../common/Catalyst/divider';
import { Subheading } from '../common/Catalyst/heading';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';

interface Props {
  hoaId: string;
}

type Which = 'balance-sheet' | 'income-statement' | 'trial-balance';

/**
 * A report the books cannot answer, shown as the refusal it is.
 *
 * Prior-period requests and a balance sheet with opening balances
 * pending both fail on purpose: returning zeros would tell a board the
 * association owned nothing and owed nothing, and they have no way to
 * know that is false — an empty report and a genuinely empty
 * association look identical. So the backend's own message is
 * rendered, which is precisely what a naive implementation would
 * replace with an empty table.
 */
const Refusal: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
    <p className="text-sm text-amber-800 dark:text-amber-300">{message}</p>
  </div>
);

/** A label and its figures, right-aligned so a column of money scans. */
const Line: React.FC<{
  label: string;
  values: (string | null | undefined)[];
  strong?: boolean;
  indent?: boolean;
}> = ({ label, values, strong, indent }) => (
  <div
    className={[
      'flex items-baseline gap-3 py-0.5 text-sm text-zinc-950 dark:text-white',
      strong ? 'font-semibold' : '',
    ].join(' ')}
  >
    <span className={['flex-1 truncate', indent ? 'pl-4' : ''].join(' ')}>
      {label}
    </span>
    {values.map((v, i) => (
      <span key={i} className="w-28 shrink-0 text-right tabular-nums">
        {formatMoney(v)}
      </span>
    ))}
  </div>
);

const ColumnHeads: React.FC<{ heads: string[] }> = ({ heads }) => (
  <div className="flex items-baseline gap-3 py-1 text-xs font-medium text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
    <span className="flex-1" />
    {heads.map((h) => (
      <span key={h} className="w-28 shrink-0 text-right">
        {h}
      </span>
    ))}
  </div>
);

const IncomeSection: React.FC<{ section: IncomeStatementSection }> = ({
  section,
}) => (
  <div className="mt-6">
    <Subheading>{section.fund_name}</Subheading>
    <ColumnHeads heads={['Period', 'Budget', 'YTD', 'YTD budget']} />

    <h5 className="mt-3 text-sm font-medium text-zinc-950 dark:text-white">
      Income
    </h5>
    {section.income.map((l) => (
      <Line
        key={l.account_number}
        label={`${l.account_number} - ${l.account_name}`}
        values={[l.period_amount, l.period_budget, l.ytd_amount, l.ytd_budget]}
        indent
      />
    ))}
    <Line
      label="Total income"
      values={[
        section.total_income_period,
        section.total_income_budget_period,
        section.total_income_ytd,
        section.total_income_budget_ytd,
      ]}
      strong
    />

    <h5 className="mt-3 text-sm font-medium text-zinc-950 dark:text-white">
      Expenses
    </h5>
    {section.expenses.map((l) => (
      <Line
        key={l.account_number}
        label={`${l.account_number} - ${l.account_name}`}
        values={[l.period_amount, l.period_budget, l.ytd_amount, l.ytd_budget]}
        indent
      />
    ))}
    <Line
      label="Total expenses"
      values={[
        section.total_expenses_period,
        section.total_expenses_budget_period,
        section.total_expenses_ytd,
        section.total_expenses_budget_ytd,
      ]}
      strong
    />

    <Line
      label={`Net — ${section.fund_name}`}
      values={[
        section.net_period,
        section.net_budget_period,
        section.net_ytd,
        section.net_budget_ytd,
      ]}
      strong
    />
  </div>
);

/** One trial-balance account, with its entries expandable. */
const TrialRow: React.FC<{ row: TrialBalanceRow }> = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-zinc-100 dark:border-zinc-800">
      <div className="flex items-baseline gap-3 py-1 text-sm text-zinc-950 dark:text-white">
        <button
          type="button"
          className="flex-1 truncate text-left hover:underline"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? '▾' : '▸'} {row.account_number} - {row.account_name}
        </button>
        {[
          row.opening_balance,
          row.period_debits,
          row.period_credits,
          row.ending_balance,
        ].map((v, i) => (
          <span key={i} className="w-28 shrink-0 text-right tabular-nums">
            {formatMoney(v)}
          </span>
        ))}
      </div>
      {/*
        The detail is what makes this report 4 of the packet rather than
        a summary: a trial balance is what an accountant traces a figure
        through, and one without its entries cannot be traced.
      */}
      {open && (
        <div className="pb-2">
          {row.entries.map((e) => (
            <div
              key={e.entry_id + e.debit + e.credit}
              className="flex items-baseline gap-3 py-0.5 text-xs text-zinc-600 dark:text-zinc-400"
            >
              <span className="flex-1 truncate pl-6">
                {formatBillingDate(e.effective_date)} {e.description}
              </span>
              <span className="w-28 shrink-0 text-right tabular-nums" />
              <span className="w-28 shrink-0 text-right tabular-nums">
                {formatMoney(e.debit)}
              </span>
              <span className="w-28 shrink-0 text-right tabular-nums">
                {formatMoney(e.credit)}
              </span>
              <span className="w-28 shrink-0" />
            </div>
          ))}
          {row.entries.length === 0 && (
            <p className="pl-6 text-xs text-zinc-500 dark:text-zinc-400">
              No entries in this period.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Three reports, a period picker, and nothing else.
 *
 * Each is a faithful rendering of its endpoint. The reports read the
 * journal and nothing else, and a screen that tidied a number would be
 * believed over the books.
 */
const ReportsPanel: React.FC<Props> = ({ hoaId }) => {
  const [periods, setPeriods] = useState<AccountingPeriodReturn[]>([]);
  const [periodId, setPeriodId] = useState('');
  const [which, setWhich] = useState<Which>('balance-sheet');

  const [balanceSheet, setBalanceSheet] = useState<BalanceSheet | null>(null);
  const [income, setIncome] = useState<IncomeStatement | null>(null);
  const [trial, setTrial] = useState<TrialBalance | null>(null);

  const [loading, setLoading] = useState(true);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await ledgerService.listPeriods({ hoaId });
        const real = data.periods.filter((p) => !p.is_opening);
        setPeriods(real);
        setPeriodId(real[real.length - 1]?.id ?? '');
      } catch (err) {
        setRefusal(billingErrorMessage(err, "Couldn't load the periods."));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [hoaId]);

  const period = periods.find((p) => p.id === periodId) ?? null;

  /**
   * Fetch the selected report.
   *
   * **Guarded against its own staleness.** Switching report or period
   * starts a second request while the first is in flight, and without
   * the guard whichever the server answers last wins — so a slow
   * balance sheet could land on top of a trial balance the person is
   * already reading, or a refusal could outlive the request it
   * refused. Neither is visibly wrong, which is what makes it worth
   * the guard: the page would just be showing the wrong month's
   * figures under the right heading.
   */
  const load = useCallback(
    async (isStale: () => boolean) => {
      if (!period) return;
      setRefusal(null);
      setBalanceSheet(null);
      setIncome(null);
      setTrial(null);
      try {
        if (which === 'balance-sheet') {
          const { data } = await ledgerService.getBalanceSheet({
            hoaId,
            asOf: period.end_date,
          });
          if (isStale()) return;
          setBalanceSheet(data);
        } else if (which === 'income-statement') {
          const { data } = await ledgerService.getIncomeStatement({
            hoaId,
            periodId: period.id,
          });
          if (isStale()) return;
          setIncome(data);
        } else {
          const { data } = await ledgerService.getTrialBalance({
            hoaId,
            periodId: period.id,
          });
          if (isStale()) return;
          setTrial(data);
        }
      } catch (err) {
        if (isStale()) return;
        setRefusal(billingErrorMessage(err, 'The books cannot answer that.'));
      }
    },
    [hoaId, period, which]
  );

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <FieldPicker
          className="w-full max-w-xs"
          label="Report"
          value={which}
          onChange={(v) => setWhich(v as Which)}
          options={[
            { value: 'balance-sheet', label: 'Balance sheet' },
            {
              value: 'income-statement',
              label: 'Statement of revenues and expenses',
            },
            { value: 'trial-balance', label: 'Trial balance' },
          ]}
        />
        <FieldPicker
          className="w-full max-w-xs"
          label="Accounting period"
          value={periodId}
          onChange={setPeriodId}
          options={periods.map((p) => ({
            value: p.id,
            label: `FY${p.fiscal_year} P${p.period_number} · ${formatBillingDate(
              p.end_date
            )}`,
          }))}
        />
        <Button plain onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <Divider className="my-5" />

      {refusal && <Refusal message={refusal} />}

      {balanceSheet && (
        <div data-testid="balance-sheet">
          <Subheading>
            Balance sheet as of {formatBillingDate(balanceSheet.as_of)}
          </Subheading>
          <div className="mt-4">
            <h5 className="text-sm font-medium text-zinc-950 dark:text-white">
              Assets
            </h5>
            {balanceSheet.assets.map((l) => (
              <Line
                key={l.account_number}
                label={`${l.account_number} - ${l.account_name}`}
                values={[l.amount]}
                indent
              />
            ))}
            <Line
              label="Total assets"
              values={[balanceSheet.total_assets]}
              strong
            />

            <h5 className="mt-4 text-sm font-medium text-zinc-950 dark:text-white">
              Liabilities
            </h5>
            {balanceSheet.liabilities.map((l) => (
              <Line
                key={l.account_number}
                label={`${l.account_number} - ${l.account_name}`}
                values={[l.amount]}
                indent
              />
            ))}
            <Line
              label="Total liabilities"
              values={[balanceSheet.total_liabilities]}
              strong
            />

            <h5 className="mt-4 text-sm font-medium text-zinc-950 dark:text-white">
              Equity
            </h5>
            {balanceSheet.equity.map((l) => (
              <Line
                key={l.account_number}
                label={`${l.account_number} - ${l.account_name}`}
                values={[l.amount]}
                indent
              />
            ))}
            {/*
              A computed line, never an account — income and expense
              accounts still carry the year's activity, and this is that
              activity taking its place in equity. It is why the sheet
              balances before a year is closed.
            */}
            <Line
              label="Net income (year to date)"
              values={[balanceSheet.net_income]}
              indent
            />
            <Line
              label="Total equity"
              values={[balanceSheet.total_equity]}
              strong
            />

            <div className="mt-4">
              <Line
                label="Total liabilities and equity"
                values={[balanceSheet.total_liabilities_and_equity]}
                strong
              />
            </div>

            {!balanceSheet.balanced && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                This balance sheet does not balance. Assets should equal
                liabilities plus equity; that they do not is a defect in the
                books, not in the report.
              </p>
            )}
          </div>
        </div>
      )}

      {income && (
        <div data-testid="income-statement">
          <Subheading>
            Statement of revenues and expenses ·{' '}
            {formatBillingDate(income.period_start)} –{' '}
            {formatBillingDate(income.period_end)}
          </Subheading>
          {income.sections.map((s) => (
            <IncomeSection key={s.fund_id} section={s} />
          ))}
          {income.sections.length === 0 && (
            <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
              No fund had activity in this period.
            </p>
          )}
          <Divider className="my-4" />
          {/*
            A month whose only inter-fund movement was the reserve
            contribution nets to zero here, because the paired transfer
            accounts cancel. That is the packet's own Net Total line.
          */}
          <Line
            label="Net total — all funds"
            values={[income.net_total_period, null, income.net_total_ytd]}
            strong
          />
        </div>
      )}

      {trial && (
        <div data-testid="trial-balance">
          <div className="flex items-center justify-between gap-3">
            <Subheading>
              Trial balance · {formatBillingDate(trial.period_start)} –{' '}
              {formatBillingDate(trial.period_end)}
            </Subheading>
            <Badge color={trial.balanced ? 'green' : 'red'}>
              {trial.balanced ? 'balanced' : 'out of balance'}
            </Badge>
          </div>

          {trial.opening_balances_pending && (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
              Opening balances have not been posted. Every figure below is this
              period's activity only, with nothing brought forward.
            </p>
          )}

          <div className="mt-4">
            <ColumnHeads heads={['Opening', 'Debits', 'Credits', 'Ending']} />
            {trial.rows.map((r) => (
              <TrialRow key={r.account_id} row={r} />
            ))}
            <div className="pt-2">
              <Line
                label="Totals"
                values={[null, trial.total_debits, trial.total_credits, null]}
                strong
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPanel;
